import { useState, useCallback } from 'react';
import { message } from 'antd';
import pwp253Api, { FP630, FP631 } from '../../../services/pwp253Api';

export function usePWP253Record() {
  const [record, setRecord] = useState<FP630 | null>(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async (id: string): Promise<FP630 | null> => {
    setLoading(true);
    try {
      const data = await pwp253Api.get(id);
      setRecord(data);
      return data;
    } catch {
      message.error('載入失敗');
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const save = useCallback(async (
    master: Partial<FP630>,
    details: Partial<FP631>[],
    isNew: boolean
  ): Promise<FP630 | null> => {
    setLoading(true);
    try {
      let result: FP630;
      if (isNew) {
        result = await pwp253Api.create(master, details);
        message.success('新增成功');
      } else {
        result = await pwp253Api.update(master.fp630_01!, master, details);
        message.success('儲存成功');
      }
      setRecord(result);
      return result;
    } catch (err: any) {
      if (err.response?.status === 422) {
        const errors = err.response.data.errors;
        if (errors?.length > 0) {
          message.error(errors.map((e: any) => e.message).join('\n'));
        }
        return null;
      }
      message.error('儲存失敗');
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const remove = useCallback(async (id: string): Promise<boolean> => {
    setLoading(true);
    try {
      await pwp253Api.delete(id);
      message.success('刪除成功');
      setRecord(null);
      return true;
    } catch {
      message.error('刪除失敗');
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  const clear = useCallback(() => setRecord(null), []);

  return { record, loading, load, save, remove, clear, setRecord };
}
